import { alpha, Theme } from "@material-ui/core/styles";

export const getDateFromMEAValue = (value: number | string | undefined) => {
  if (!value) return undefined;
  const intValue = typeof value === "number" ? value : Number(value);
  if (isNaN(intValue)) {
    return undefined;
  }
  try {
    return new Date(Math.round((intValue - 25569) * 86400 * 1000));
  } catch (e) {
    return undefined;
  }
};
export const formatDate = (
  date: Date | undefined,
  locale: string = "en-GB",
  hasDay: boolean = true,
) => {
  if (!date) {
    return "";
  }
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    year: "numeric",
    day: hasDay ? "2-digit" : undefined,
  }).format(date);
};

export const formatPerformancePercentage = (value: unknown) => {
  if (typeof value === "object") return "-";
  if (typeof value === "undefined") return "-";
  if (value === null) return "-";
  if (typeof value !== "string") return "-";
  const n = Number.parseFloat(value);
  if (isNaN(n)) return "-";
  return `${Math.round(n)} %`;
};

export const normalizeWFStatus = (value?: string) => {
  const normalizedValue = (value || "").trim().toLowerCase();

  if (!normalizedValue) return "";
  if (["dataentry", "waiting for input"].includes(normalizedValue)) {
    return "waiting_for_input";
  }
  if (["approval", "waiting for approval"].includes(normalizedValue)) {
    return "waiting_for_approval";
  }
  if (
    ["finalapproval", "final approval", "waiting for final approval"].includes(
      normalizedValue,
    )
  ) {
    return "waiting_for_final_approval";
  }
  if (["approvalrejected", "approval rejected"].includes(normalizedValue)) {
    return "approval_rejected";
  }
  if (
    ["finalapprovalrejected", "final approval rejected"].includes(
      normalizedValue,
    )
  ) {
    return "final_approval_rejected";
  }
  if (["draft saved", "draft"].includes(normalizedValue)) return "draft_saved";

  return normalizedValue.replace(/\s+/g, "_");
};

export const formatWFStatus = (value: string | undefined) => {
  const normalizedValue = normalizeWFStatus(value);

  if (!normalizedValue) return "-";
  if (normalizedValue === "waiting_for_input") return "Waiting for input";
  if (normalizedValue === "waiting_for_approval") return "Waiting for approval";
  if (normalizedValue === "waiting_for_final_approval") {
    return "Waiting for final approval";
  }
  if (normalizedValue === "approval_rejected") return "Approval rejected";
  if (normalizedValue === "final_approval_rejected") {
    return "Final approval rejected";
  }
  if (normalizedValue === "draft_saved") return "Draft saved";

  return normalizedValue
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const createBadgeStyle = (
  color: string,
  options?: {
    textOpacity?: number;
    backgroundOpacity?: number;
    borderOpacity?: number;
  },
) => ({
  color: alpha(color, options?.textOpacity ?? 0.92),
  backgroundColor: alpha(color, options?.backgroundOpacity ?? 0.12),
  border: `1px solid ${alpha(color, options?.borderOpacity ?? 0.22)}`,
});

export const getWFStatusBadgeStyle = (value?: string, theme?: Theme) => {
  const normalizedValue = normalizeWFStatus(value);
 
  if (!normalizedValue || normalizedValue === "waiting_for_input") {
    return createBadgeStyle(theme?.palette.text.primary ?? "#62676d", {
      textOpacity: 0.6,
      backgroundOpacity: 0.05,
      borderOpacity: 0.14,
    });
  }

  if (normalizedValue.includes("rejected")) {
    return createBadgeStyle(theme?.palette.warning.dark ?? "#b54708", {
      textOpacity: 0.9,
      backgroundOpacity: 0.16,
      borderOpacity: 0.28,
    });
  }

  if (
    ["waiting_for_approval", "waiting_for_final_approval"].includes(
      normalizedValue,
    )
  ) {
    return createBadgeStyle(theme?.palette.primary.dark ?? "#175cd3", {
      textOpacity: 0.94,
      backgroundOpacity: 0.14,
      borderOpacity: 0.24,
    });
  }

  if (["draft_saved"].includes(normalizedValue)) {
    return createBadgeStyle(theme?.palette.success.dark ?? "#027a48", {
      textOpacity: 0.9,
      backgroundOpacity: 0.14,
      borderOpacity: 0.24,
    });
  }

  return createBadgeStyle(theme?.palette.text.primary ?? "#62676d", {
    textOpacity: 0.78,
    backgroundOpacity: 0.05,
    borderOpacity: 0.14,
  });
};
